import { useState } from 'react'
import { Link2 } from 'lucide-react'
import { useGrants, usePeople } from '@/db/hooks'
import { hashSecret, newSecret } from '@/kinds/grant'
import { memberNameWith } from '@/lib/people'
import { nudgeMembersPatch } from '@/lib/follow'
import { linkExpiry, linkScope, routeFor, sharedNote, type ShareItem, type ShareRoute } from '@/lib/shareWith'
import { mutate } from '@/runtime'
import { useAccess } from '@/state/access'
import { Avatar } from '@/components/PersonBits'
import { ShareSheet } from '@/components/ShareSheet'
import { ActionTrigger, type ActionAs, type ActionItem } from '@/components/ActionBar'
import { Button, Sheet } from '@/components/ui'

/**
 * "Share…": the one entry point for sharing an item. People come first, each
 * reached the way they connect; below them "Make a link" (a list, page or
 * project) opens the grant sheet for a link to the item itself. Starting from
 * the People app: Each person is reached the way they connect (lib/shareWith.ts):
 * a login to the item's suite puts it in their app and pushes them (the
 * `nudge_members` label with their grant id, the server's one push path); a
 * capture link gets a line on their page, with a read-only link to the item, and
 * no push; anyone else is offered a link. The owner's devices only.
 */
export const ShareWith = ({ item, as }: { item: ShareItem; as?: ActionAs }) => {
  const isMember = useAccess((s) => s.member)
  const [open, setOpen] = useState(false)
  const [linking, setLinking] = useState<{ name: string; key: string } | null>(null)
  const [itemLink, setItemLink] = useState(false)
  if (isMember) return null
  return (
    <>
      <ActionTrigger as={as} label="Share…" onClick={() => setOpen(true)} testid="share-with" />
      {open && (
        <ShareWithSheet
          item={item}
          onClose={() => setOpen(false)}
          onLink={(p) => {
            setOpen(false)
            setLinking(p)
          }}
          onItemLink={() => {
            setOpen(false)
            setItemLink(true)
          }}
        />
      )}
      {linking && <ShareSheet person={linking} onClose={() => setLinking(null)} />}
      {itemLink && isLinkable(item) && (
        <ShareSheet
          target={{ kind: item.kind, id: item.id, name: item.title }}
          onClose={() => setItemLink(false)}
        />
      )}
    </>
  )
}

/** "Share with…" as an ActionBar item: the bar places it, this owns the sheets. Owner only. */
export const useShareWithItem = (item: ShareItem | null): ActionItem => {
  const isMember = useAccess((s) => s.member)
  return {
    id: 'share-with',
    label: 'Share…',
    hidden: isMember || !item,
    render: ({ as }) => (item ? <ShareWith item={item} as={as} /> : null),
  }
}

/** A list, page or project can be linked to as itself; a task or date cannot. */
const isLinkable = (item: ShareItem): item is ShareItem & { kind: 'List' | 'Page' | 'Project' } =>
  item.kind === 'List' || item.kind === 'Page' || item.kind === 'Project'

const ShareWithSheet = ({
  item,
  onClose,
  onLink,
  onItemLink,
}: {
  item: ShareItem
  onClose: () => void
  onLink: (p: { name: string; key: string }) => void
  onItemLink: () => void
}) => {
  const people = usePeople()
  const grants = useGrants()
  const [done, setDone] = useState<Record<string, string>>({})
  const [failed, setFailed] = useState<string | null>(null)
  const owner = people.find((p) => p.spec.me)?.spec.name.split(/\s+/)[0] ?? 'Alfie'
  const others = people.filter((p) => !p.deleted_at && !p.spec.me)

  const send = async (id: string, r: ShareRoute) => {
    setFailed(null)
    try {
      if (r.how === 'app') {
        await mutate.patch(item.id, { labels: nudgeMembersPatch(new Date(), r.grant.id) })
        setDone((d) => ({ ...d, [id]: 'Sent to their app' }))
      } else if (r.how === 'link') {
        let url: string | undefined
        const scope = linkScope(item)
        if (scope) {
          const secret = newSecret()
          await mutate.create(
            'Grant',
            {
              name: `${item.title} for ${memberNameWith(r.grant.spec, people)}`,
              scopes: [scope],
              token_hash: await hashSecret(secret),
              expires_at: linkExpiry(),
              ...(r.grant.spec.from ? { from: r.grant.spec.from } : {}),
            },
            {},
          )
          url = `${window.location.origin}/s/${secret}`
        }
        await mutate.patch(r.grant.id, {
          spec: { reply: sharedNote(owner, item, url), reply_at: new Date().toISOString() },
        })
        setDone((d) => ({ ...d, [id]: 'Left on their page' }))
      }
    } catch (e) {
      setFailed((e as Error).message)
    }
  }

  return (
    <Sheet open onClose={onClose} title="Share…">
      <ul className="group-list" data-testid="share-with-list">
        {others.length === 0 && <li className="px-4 py-3 text-sm text-muted">No one in People yet.</li>}
        {others.map((p) => {
          const r = routeFor(p, grants, item)
          const first = p.spec.name.split(/\s+/)[0]
          const sent = done[p.id]
          const line =
            sent ??
            (r.how === 'app'
              ? `Goes in ${first}’s app and notifies ${first}`
              : r.how === 'link'
                ? `Left on ${first}’s capture page, no notification${r.grant.spec.reply ? ' (replaces your note there)' : ''}`
                : r.how === 'elsewhere'
                  ? item.kind === 'Date'
                    ? `${first}’s app doesn’t carry dates`
                    : `${first}’s app doesn’t include this item’s suite`
                  : `${first} has no link or app yet`)
          return (
            <li
              key={p.id}
              className="flex items-center gap-3 px-4 min-h-[44px] py-2"
              data-testid={`share-with-${p.spec.key}`}
            >
              <Avatar person={p} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] truncate">{p.spec.name}</p>
                <p className="text-xs text-muted">{line}</p>
              </div>
              {sent ? null : r.how === 'none' ? (
                <Button
                  variant="chip"
                  className="min-h-11!"
                  onClick={() => onLink({ name: p.spec.name, key: p.spec.key })}
                  data-testid="share-with-make-link"
                >
                  Make a link
                </Button>
              ) : r.how === 'elsewhere' ? null : (
                <Button
                  variant="chip"
                  className="min-h-11!"
                  onClick={() => void send(p.id, r)}
                  data-testid="share-with-send"
                >
                  {r.how === 'app' ? 'Share' : 'Leave a note'}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
      {isLinkable(item) && (
        <ul className="group-list mt-3">
          <li>
            <ActionTrigger
              as="row"
              icon={Link2}
              label="Make a link"
              onClick={onItemLink}
              testid="share-with-link"
            />
          </li>
        </ul>
      )}
      {failed && <p className="text-sm text-rose-300 mt-3">{failed}</p>}
    </Sheet>
  )
}
